import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, CheckCircle2, ShoppingCart } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

interface DomainPurchaseModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  domain: {
    domain: string
    available?: boolean
    premium?: boolean
    pricing?: {
      register?: { price: number; currency: string }
      [key: string]: any
    }
  }
  onPurchaseComplete?: () => void
  clientId?: string | null
  clientName?: string | null
}

const EXPECTED_CONFIRMATION_CODE = 'I_CONFIRM_PURCHASE_DOMAIN'

export function DomainPurchaseModal({
  open,
  onOpenChange,
  domain,
  onPurchaseComplete,
  clientId,
  clientName,
}: DomainPurchaseModalProps) {
  const [confirmText, setConfirmText] = useState('')
  const [requireChargeAck, setRequireChargeAck] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setConfirmText('')
      setRequireChargeAck(false)
      setError(null)
    }
  }, [open])

  const handlePurchase = async () => {
    setError(null)

    // Verification 1: Domain name must be re-typed exactly
    if (confirmText !== domain.domain) {
      setError('Domain name does not match. Please type the domain name exactly as shown.')
      return
    }

    // Verification 2: Charge acknowledgment must be checked
    if (!requireChargeAck) {
      setError('You must acknowledge that you will be charged for this purchase.')
      return
    }

    setIsPurchasing(true)

    try {
      // Use fetch directly to get access to the full response body
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: { session } } = await supabase.auth.getSession()
      const accessToken = session?.access_token

      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration missing')
      }

      const functionUrl = `${supabaseUrl}/functions/v1/purchase-domain`
      
      const normalizedClientName = clientName?.trim() || null
      const normalizedClientId = clientId?.trim ? clientId.trim() : clientId || null

      const payload = {
        domainName: domain.domain,
        confirmPurchase: true,
        confirmationCode: EXPECTED_CONFIRMATION_CODE,
        source: 'ui_button',
        clientId: normalizedClientId,
        clientName: normalizedClientName,
      }

      console.log('Purchase payload:', payload)

      const fetchResponse = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify(payload),
      })

      const responseData = await fetchResponse.json()
      console.log('Purchase response status:', fetchResponse.status)
      console.log('Purchase response data:', responseData)

      // Check if response indicates failure
      if (!fetchResponse.ok) {
        const errorMessage = responseData?.error || responseData?.message || `Purchase failed with status ${fetchResponse.status}`
        console.error('Purchase failed:', errorMessage)
        setError(errorMessage)
        toast.error(errorMessage)
        return
      }

      // Check if data indicates failure
      if (responseData && typeof responseData === 'object') {
        if (!responseData.success) {
          const errorMsg = responseData.error || 'Purchase failed'
          setError(errorMsg)
          toast.error(errorMsg)
          return
        }
      }

      toast.success(`Domain ${domain.domain} purchased successfully!`)
      onPurchaseComplete?.()
      onOpenChange(false)
    } catch (err: any) {
      console.error('Purchase exception:', err)
      console.error('Exception details:', JSON.stringify(err, null, 2))
      
      // Try to extract error from exception
      let errorMessage = 'Failed to purchase domain. Please try again.'
      if (err?.message) {
        errorMessage = err.message
      } else if (err?.error) {
        errorMessage = err.error
      }
      
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsPurchasing(false)
    }
  }

  const registrationPrice = domain.pricing?.register?.price || 9.98
  const currency = domain.pricing?.register?.currency || 'USD'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Purchase Domain: {domain.domain}
          </DialogTitle>
          <DialogDescription>
            Complete the verification steps below to purchase this domain. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Domain Info */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Domain:</span>
                <span className="text-sm font-mono">{domain.domain}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Client:</span>
                <span className="text-sm font-semibold">{clientName?.trim() || 'Unassigned'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Price:</span>
                <span className="text-sm font-semibold">
                  {currency} ${registrationPrice.toFixed(2)}
                </span>
              </div>
              {domain.premium && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Type:</span>
                  <span className="text-sm text-amber-600">Premium Domain</span>
                </div>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Verification Step 1: Re-type domain */}
          <div className="space-y-2">
            <Label htmlFor="confirm-text" className="text-sm font-semibold">
              Verification Step 1: Re-type the domain name
            </Label>
            <Input
              id="confirm-text"
              placeholder={`Type "${domain.domain}" to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isPurchasing}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Type the domain name exactly as shown above to confirm you want to purchase it.
            </p>
          </div>

          {/* Verification Step 2: Acknowledge charges */}
          <div className="space-y-2">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="charge-ack"
                checked={requireChargeAck}
                onCheckedChange={(checked) => setRequireChargeAck(checked === true)}
                disabled={isPurchasing}
              />
              <div className="space-y-1">
                <Label htmlFor="charge-ack" className="text-sm font-semibold cursor-pointer">
                  Verification Step 2: Acknowledge charges
                </Label>
                <p className="text-xs text-muted-foreground">
                  I understand that I will be charged {currency} ${registrationPrice.toFixed(2)} for this domain purchase.
                  This charge is non-refundable.
                </p>
              </div>
            </div>
          </div>

          {/* Purchase Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPurchasing}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={
                isPurchasing ||
                confirmText !== domain.domain ||
                !requireChargeAck
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Purchasing...
                </>
              ) : (
                <>
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Purchase Domain
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
